import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { usePlayer } from './Player.hooks'
import PlayerChapters from './chapters/PlayerChapters'

import Header from './header'
import PlayerBar from './player-bar'
import ModalVoiceHelp from './voice-help/ModalVoiceHelp'
import ModalVoiceConfirm from './voice-help/ModalVoiceConfirm'
import ModalPreviewLimit from './preview-limit'
import { usePlayerContext } from 'context/player'
import * as playerActions from 'context/player/reducer/player.actions'
import { useTranslation } from 'i18n'

const Player: React.FC = () => {
  const { cleanUpSound } = usePlayer()
  const { t } = useTranslation()
  const {
    dispatch,
    state: { isMinimized, isOpen, showChaptersList }
  } = usePlayerContext()
  const dialogRef = useRef<HTMLDivElement>(null)
  const elementRef = document.getElementById('player')

  const isFullScreen = isOpen && !isMinimized

  useEffect(() => {
    if (!isOpen) {
      cleanUpSound()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (!isFullScreen) return

    const rootElement = document.getElementById('root')
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    rootElement?.setAttribute('inert', '')
    dialogRef.current?.focus()

    return () => {
      rootElement?.removeAttribute('inert')
      previousFocus?.focus()
    }
  }, [isFullScreen])

  useEffect(() => {
    if (!isFullScreen || showChaptersList) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(playerActions.minimize())
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => document.removeEventListener('keydown', handleEscape)
  }, [isFullScreen, showChaptersList, dispatch])

  if (!isOpen) {
    return null
  }

  const Component = (
    <div
      ref={dialogRef}
      role={isFullScreen ? 'dialog' : undefined}
      aria-modal={isFullScreen ? 'true' : undefined}
      aria-label={isFullScreen ? t('Reprodutor de áudio') : undefined}
      tabIndex={isFullScreen ? -1 : undefined}
    >
      {!isMinimized && <Header />}
      <PlayerChapters />
      <PlayerBar />
      <ModalVoiceHelp />
      <ModalVoiceConfirm />
      <ModalPreviewLimit />
    </div>
  )

  return createPortal(Component, elementRef)
}

export default Player
